import React, { useState } from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import LaunchIcon from '@mui/icons-material/Launch';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { AnomalyCase, CaseStatus, RULE_DESCRIPTIONS, TIER_LABELS } from './types';
import { buildRatesVerifyUrl, buildVendorRoomRateDatesUrl, stayDate, suggestedCheckIn } from './verifyUrl';

const STATUS_COLOURS: Record<CaseStatus, 'error' | 'warning' | 'success' | 'default'> = {
  pending: 'error',
  acknowledged: 'warning',
  resolved: 'success',
  dismissed: 'default',
  auto_resolved: 'success',
  suppressed: 'default',
};

const CHECK_LABEL: Record<string, string> = {
  true: 'Agrees with the alert',
  false: 'Disagrees with the alert',
  null: 'Could not check',
};

interface Props {
  anomalyCase: AnomalyCase | null;
  onClose: () => void;
  onStatusChange: (caseId: string, status: CaseStatus) => void;
  onDismiss: (anomalyCase: AnomalyCase) => void;
  onSuppress: (anomalyCase: AnomalyCase) => void;
}

/**
 * Everything about one case in one place: why it was raised, what the system already
 * checked, which products and nights it covers, how to confirm each of them by hand, and
 * what has happened to it so far.
 *
 * This is the only place a case gets actioned. The rollup view aggregates these same cases
 * but is read-only, so there is one surface for doing and one for seeing shape.
 */
export default function CaseDetailModal({ anomalyCase: c, onClose, onStatusChange, onDismiss, onSuppress }: Props) {
  const [checkInByProduct, setCheckInByProduct] = useState<Record<string, string>>({});

  if (!c) {
    return null;
  }

  const keyOf = (p: { roomTypeCode: string; ratePlanCode: string }) => `${p.roomTypeCode}|${p.ratePlanCode}`;

  return (
    <Dialog open={!!c} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={RULE_DESCRIPTIONS[c.ruleKey] ?? ''}>
            <Typography variant="h6" component="span">
              {c.ruleLabel}
            </Typography>
          </Tooltip>
          <Chip size="small" label={c.status} color={STATUS_COLOURS[c.status]} />
          {c.propertyTier === 'tier_1' && <Chip size="small" color="primary" label={TIER_LABELS[c.propertyTier]} />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {c.caseId} · {c.propertyName} · {c.vendorBrand} · {c.territory} · {c.channel} · hotel {c.hotelCode}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {c.dismissReason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Dismissed — {c.dismissReason}
          </Alert>
        )}

        {/* What the system checked before raising this, so nobody redoes it by hand */}
        <Typography variant="subtitle2" gutterBottom>
          Pre-validation
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          {c.preValidation.checks.map((chk) => (
            <Paper key={chk.label} variant="outlined" sx={{ p: 1.5, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {chk.label}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={chk.supportsAlert ? 'error' : !chk.supportsAlert ? 'success' : 'warning'}
                  label={CHECK_LABEL[String(chk.supportsAlert)]}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {chk.detail}
              </Typography>
            </Paper>
          ))}
        </Stack>

        {/* The particulars: every product the case covers, each confirmable on its own */}
        <Typography variant="subtitle2" gutterBottom>
          Affected products ({c.affectedProducts.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Room / rate</TableCell>
                <TableCell align="right">Booked</TableCell>
                <TableCell>Affected nights</TableCell>
                <TableCell>Last known rate</TableCell>
                <TableCell>Check night</TableCell>
                <TableCell align="right">Confirm</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {c.affectedProducts.map((p) => {
                const k = keyOf(p);
                const checkIn = checkInByProduct[k] ?? suggestedCheckIn(p.firstStayDate, p.lastStayDate);
                return (
                  <TableRow key={k} hover>
                    <TableCell>
                      <Typography variant="body2">{p.ratePlanName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.roomTypeName} · {p.roomTypeCode} / {p.ratePlanCode}
                      </Typography>
                      {p.hasMealPlan && <Chip size="small" variant="outlined" label="meal plan" sx={{ ml: 1 }} />}
                    </TableCell>

                    <TableCell align="right">
                      {p.rateRank ? (
                        <Tooltip title={`${p.bookings12m} bookings in the last 12 months`}>
                          <Chip size="small" color={p.rateRank <= 3 ? 'error' : 'default'} label={`#${p.rateRank}`} />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          never
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{p.nightsAffected}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stayDate(p.firstStayDate) ?? '—'} → {stayDate(p.lastStayDate) ?? '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {p.lastKnownRate != null ? (
                        <>
                          <Typography variant="body2">AUD {p.lastKnownRate}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            seen {p.lastRateSeenAt?.slice(0, 10)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          never priced
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <TextField
                        type="date"
                        size="small"
                        value={checkIn ?? ''}
                        onChange={(e) => setCheckInByProduct((prev) => ({ ...prev, [k]: e.target.value }))}
                        inputProps={{ min: stayDate(p.firstStayDate), max: stayDate(p.lastStayDate) }}
                        sx={{ width: 160 }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Live customer-facing search for this night">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!checkIn}
                              endIcon={<LaunchIcon />}
                              href={
                                checkIn
                                  ? buildRatesVerifyUrl({
                                      accommodationPropertyId: c.accommodationPropertyId,
                                      checkIn,
                                    })
                                  : undefined
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Rate
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="What we hold, day by day">
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<CalendarMonthIcon />}
                            href={buildVendorRoomRateDatesUrl({
                              vendorId: c.vendorId,
                              propertyId: c.propertyId,
                              roomTypeId: c.roomTypeId,
                              roomRateId: c.roomRateId,
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Dates
                          </Button>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" gutterBottom>
          History ({c.occurrences} {c.occurrences === 1 ? 'occurrence' : 'occurrences'})
        </Typography>
        <Box sx={{ pl: 1 }}>
          {c.history.map((h) => (
            <Box key={`${h.at}-${h.summary}`} sx={{ display: 'flex', gap: 2, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
                {h.at.replace('T', ' ').slice(0, 16)}
              </Typography>
              <Chip size="small" variant="outlined" label={h.actor} sx={{ minWidth: 72 }} />
              <Typography variant="body2">{h.summary}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Divider orientation="vertical" flexItem />
        <Button
          startIcon={<CheckCircleOutlineIcon />}
          disabled={c.status !== 'pending'}
          onClick={() => onStatusChange(c.caseId, 'acknowledged')}
        >
          Acknowledge
        </Button>
        <Button
          color="success"
          startIcon={<DoneAllIcon />}
          disabled={c.status === 'resolved'}
          onClick={() => onStatusChange(c.caseId, 'resolved')}
        >
          Resolve
        </Button>
        <Button
          color="warning"
          startIcon={<NotInterestedIcon />}
          disabled={c.status === 'dismissed'}
          onClick={() => onDismiss(c)}
        >
          Dismiss
        </Button>
        <Tooltip title="Suppress — silence this shape of alert for a while">
          <Button
            color="warning"
            startIcon={<VolumeOffIcon />}
            disabled={c.status === 'suppressed'}
            onClick={() => onSuppress(c)}
          >
            Suppress
          </Button>
        </Tooltip>
        <Box flex={1} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
